// src/services/sync/writers.js
// Typed write functions. Every mutation goes through here.
//
// Pattern per writer:
//   1. Validate payload against schema
//   2. Apply optimistic update to Zustand store immediately
//   3. Enqueue to SyncQueue for durable background delivery
//   4. Return { syncId, <typed object>, optimistic: true }
//
// The engine handles delivery, retry, and conflict resolution.
// Components never await the server — they see the update instantly.

import { enqueue }     from './engine.js';
import { getDeviceId } from '../../utils/deviceId.js';
import {
  createUserReport,
  reportFromSOSForm,
  validateUserReport,
}                           from '@data/schemas/UserReport';
import { createIncident, validateIncident } from '@data/schemas/Incident';
import {
  runModerationChecks,
  checkRateLimit,
  recordPostSubmission,
  validateSOSPost,
  recordSOSSubtype,
  recordPostForDedupe,
  filterProfanity,
}                           from '@services/moderation';

// Zustand store — imported lazily to avoid circular deps at module load
async function _getPostsStore() {
  const { usePostsStore } = await import('@store');
  return usePostsStore.getState();
}

// ── SOS (priority 5, up to 10 attempts) ──────────────────────────
export async function writeSOS({
  sosSubtype, sosUrgency, body, lat, lon, locationAccuracyM, address, zone, mediaUrls = [],
}) {
  const report = reportFromSOSForm({ sosSubtype, sosUrgency, body, lat, lon, locationAccuracyM, address, zone, mediaUrls });
  report.deviceId = getDeviceId();

  // Populate authorId + uid from current Firebase auth user
  try {
    const { initFirebase } = await import('@services/storage/firebase');
    const { auth } = await initFirebase();
    const uid = auth?.currentUser?.uid ?? null;
    report.authorId = uid;
    report.uid      = uid;
  } catch {}

  const schema = validateUserReport(report);
  if (!schema.valid) throw new Error('SOS invalid: ' + schema.errors.join('; '));

  const sos = validateSOSPost(report);
  if (!sos.valid) throw new Error(sos.reason);

  // Note: validateSOSPost already checks SOS rate limit internally.
  // Don't call checkRateLimit() here — SOS must not consume general post quota.
  recordPostSubmission(true);  // records _lastSOS timestamp
  recordSOSSubtype(sosSubtype);  // device dedup by subtype

  // Optimistic update — appears in feed immediately
  const store = await _getPostsStore();
  store.addPost(report);

  const syncId = await enqueue('create_sos', report, {
    priority:    5,
    maxAttempts: 10,
    syncId:      'sos-' + report.id,
  });

  return { syncId, report, optimistic: true };
}

// ── Emergency report — flood, fire, hazmat (priority 4) ──────────
export async function writeEmergencyReport(data) {
  const report = createUserReport({ ...data, source: 'app' });

  const schema = validateUserReport(report);
  if (!schema.valid) throw new Error('Report invalid: ' + schema.errors.join('; '));

  const rate = checkRateLimit(true);
  if (!rate.allowed) throw new Error(rate.reason);
  recordPostSubmission(true);
  recordPostForDedupe(report.body, report.lat, report.lon, report.zone);

  const store = await _getPostsStore();
  store.addPost(report);

  const syncId = await enqueue('create_report', report, { priority: report.severity });
  return { syncId, report, optimistic: true };
}

// ── Community post — road, power, info (priority 1-3) ─────────────
export async function writeCommunityPost({ body, type, zone, lat, lon }) {
  const report = createUserReport({ body, type, zone, lat, lon, source: 'app' });

  const schema = validateUserReport(report);
  if (!schema.valid) throw new Error(schema.errors.join('; '));

  const mod = runModerationChecks({ body: report.body, lat: report.lat, lon: report.lon, zone: report.zone });
  if (!mod.allowed) throw new Error(mod.reason);

  recordPostSubmission(false);
  recordPostForDedupe(report.body, report.lat, report.lon, report.zone);

  const store = await _getPostsStore();
  store.addPost(report);

  const syncId = await enqueue('create_report', report);
  return { syncId, report, optimistic: true };
}

// ── Gas station update (priority 2) ──────────────────────────────
export async function writeGasUpdate({
  stationId, status, priceRegular = null, waitMinutes = null,
  fuelTypes = [], hasPower = null, zone,
}) {
  if (!['open', 'busy', 'closed', 'unknown'].includes(status)) {
    throw new Error('Invalid gas status: ' + status);
  }
  if (priceRegular !== null && (priceRegular < 0 || priceRegular > 20)) {
    throw new Error('Price must be $0–$20');
  }

  const update = {
    stationId, status, priceRegular, waitMinutes,
    fuelTypes, hasPower, zone,
    reportedAt: new Date().toISOString(),
    version:    Date.now(),
  };

  // Optimistic: update gas station in cache so GasPage reflects it immediately
  window.dispatchEvent(new CustomEvent('na-gas-optimistic', { detail: update }));

  const syncId = await enqueue('update_gas', update, { priority: 2 });
  return { syncId, update, optimistic: true };
}

// ── Flood / road incident (priority 3-4) ─────────────────────────
export async function writeIncidentReport(data) {
  const incident = createIncident({ ...data, source: 'community' });

  const check = validateIncident(incident);
  if (!check.valid) throw new Error('Incident invalid: ' + check.errors.join('; '));

  const priority = ['flood', 'road_flooding', 'fire', 'hazmat'].includes(data.type) ? 4 : 3;

  // Incidents surface in the Feed as UserReports visually — also add a feed post
  const report = createUserReport({
    type:          data.type === 'road_flooding' ? 'flood' : data.type,
    body:          incident.description ?? incident.title,
    lat:           incident.lat,
    lon:           incident.lon,
    zone:          incident.zone,
    authorDisplay: 'Community',
    source:        'app',
  });

  const store = await _getPostsStore();
  store.addPost(report);

  const syncId = await enqueue('create_report', incident, { priority });
  return { syncId, incident, optimistic: true };
}

// ── Shelter update (priority 2) ───────────────────────────────────
export async function writeShelterUpdate({ shelterId, status, capacityCurrent = null, notes = null }) {
  if (!['open', 'at_capacity', 'closed', 'pending', 'unknown'].includes(status)) {
    throw new Error('Invalid shelter status: ' + status);
  }

  const update = {
    shelterId, status, capacityCurrent, notes,
    reportedAt: new Date().toISOString(),
    version:    Date.now(),
  };

  window.dispatchEvent(new CustomEvent('na-shelter-optimistic', { detail: update }));

  const syncId = await enqueue('update_shelter', update, { priority: 2 });
  return { syncId, update, optimistic: true };
}

// ── Confirm (priority 1, idempotent) ─────────────────────────────
export async function writeConfirm(reportId, currentVersion) {
  const syncId = await enqueue(
    'confirm_report',
    { reportId, version: currentVersion },
    { priority: 1, maxAttempts: 3, syncId: `confirm-${reportId}-${Date.now()}` }
  );
  return { syncId, optimistic: true };
}

// ── Flag (priority 1, idempotent) ────────────────────────────────
export async function writeFlag(reportId) {
  const syncId = await enqueue(
    'flag_report',
    { reportId },
    { priority: 1, maxAttempts: 3, syncId: `flag-${reportId}-${Date.now()}` }
  );
  return { syncId, optimistic: true };
}
